import { NavigationExtras } from '@angular/router';
import {
  IWizRoute,
  PopUpManager,
  WizNavigationManager,
  WizPopUp,
  WizEvent
} from '@wizeoo/akm-component-lib';
import { Model } from '../../model/model';
import { BaseView } from '../../framework/views/base-view';
import { SIDE_PANEL_NAME } from '../../model/enums';

/**
 * gestion des emplacements "navigables", des containers de fonctions,
 * des noms de vues et des boutons d'action
 */
export class NavigationEngine {

  protected model: Model = null;
  constructor(model: Model) {
    this.model = model;
  }

  /**
   * Moteur statique de construction des chemins complets (pour navigation) sur constructeur de l'appModule
   *
   * @param pRoutes l'arbre à construire
   * @param rootPath le chemin de départ pour construire le path des enfants
   * @Return IWizRoute[] l'arbre construit
   */
  public static buildRoute(pRoutes: IWizRoute[], rootPath: string, parent?: IWizRoute): IWizRoute[] {
    pRoutes.forEach((route: IWizRoute) => {
      if (!!rootPath) {
        rootPath += '/';
      }

      if (!!route.path) {
        route.fullPath = `${rootPath}${route.path}`;
      }

      if (parent) {
        // affectation du niveau de profondeur de la navigation
        route.level = parent.level + 1;
        // console.log ('gotParent ' + route.level, route.path);
      } else {
        route.level = 1;
      }

      if (route.children && route.children.length > 0) {
        NavigationEngine.buildRoute(route.children, route.path, route);
      }
    });

    return pRoutes;
  }


  public leftPop: WizPopUp = null; // popUp du panneau gauche hors plan
  public rightPop: WizPopUp = null; // popUp du panneau droit hors plan

  /** interception des demandes de navigation pour mémorisation du contexte */
  public navigate(dest: string | any[], extras?: NavigationExtras): void {
    const nextras: NavigationExtras = !!extras ? { ...extras } : {};

    nextras.queryParams = this.contextQueryParams;
    WizNavigationManager.navigate(dest, nextras);
  }

  /**
   * Mise à jour de l'URL avec les paramètres du context (sans rechargement de la page)
   * @param noPath en cas de navigation depuis le changement de vision, pas de reprise du path
   */
  public updateUrlContext(noPath: boolean = false): void {
    // console.log('updateUrlContext', window.location.origin, window.location.pathname);
    let newUrl;
    if(noPath) {
      newUrl = window.location.origin + '?' + new URLSearchParams(this.contextQueryParams);
    } else {
      newUrl = window.location.origin + window.location.pathname + '?' + new URLSearchParams(this.contextQueryParams);
    }
    window.history.pushState(null, null, newUrl);
  }

  /**
   * Construction des query params d'URL avec les valeurs du context stockées dans le model
   */
  private get contextQueryParams(): any {
    const result: any = {};

    if (!!this.model.businessUnitCode) {
      result.bu = this.model.businessUnitCode;
    }

    if (!!this.model.visionCode) {
      result.vision = this.model.visionCode;
    }
    return result;
  }

  /**
   * ajout de la vue courante à la pile currentViews
   * @param e WizEvent.NAVIGATE_SET_VIEW
   */
  public addView(e: WizEvent): void {
    // console.log('add view', e);
    this.model.currentViews.set(e.value.code, e.value);
    this.checkSidePanel(); // refresh éventuel des panels
  }

  /**
   * suppression de la vue courante de la pile currentViews
   * @param e WizEvent.NAVIGATE_UNREGISTER
   */
  public removeView(e: WizEvent): void {
    this.model.currentViews.delete(e.value.code);
    this.checkSidePanel(); // refresh éventuel des panels
  }

  /**
   * controle de cohérence des panels avec la vue en cours :
   * existe et bon constructeur. Sinon, refresh / hide
   */
  private checkSidePanel(): void {
    // console.log ('checkSidePanel');

    const leftFactoryToBe: any = this.identifyFactory()[0];
    const rightFactoryToBe: any = this.identifyFactory()[1];

    let leftFactory: any = null;
    let rightFactory: any = null;

    if (PopUpManager.exist(this.leftPop)) {
      // panel gauche affiché

      if (!leftFactoryToBe) {
        // et pas de future factory...
        this.leftPop.hide();
      } else {
        // future factory, on compare
        leftFactory = this.leftPop.classFactory;
        // console.log ('leftClasses', leftFactory.name, leftFactoryToBe.name);
        if (leftFactory.name === leftFactoryToBe.name) {
          // même constructeur, rien à faire...
          // TODO : éventuelle synchro des données
        } else {
          this.buildAndShowSide(SIDE_PANEL_NAME.LEFT, leftFactoryToBe);
        }
      }
    }

    if (PopUpManager.exist(this.rightPop)) {
      // panel droit affiché
      if (!rightFactoryToBe) {
        // et pas de future factory...
        this.rightPop.hide();
      } else {
        rightFactory = this.rightPop.classFactory;
        if (rightFactory.name === rightFactoryToBe.name) {
          // même constructeur, rien à faire...
          // TODO : éventuelle synchro des données
        } else {
          this.buildAndShowSide(SIDE_PANEL_NAME.RIGHT, rightFactoryToBe);
        }
      }
    }
  }

  /** alimentation du contenu du panneau side
   * @param panel code gauche / droit
   * @param classFactory composant visuel implémentant l'interface : ISidePanel
   */
  private buildAndShowSide(panel: string, classFactory: any) {
    let popToManager: WizPopUp; // pop gauche ou droite
    let targetPosition: any; // position physique

    switch (panel) {
      case SIDE_PANEL_NAME.LEFT: {
        popToManager = this.leftPop;
        targetPosition = { left: '0px', top: '35px' }; // 35px car le menu au dessus fait 34px au total
        break;
      }
      case SIDE_PANEL_NAME.RIGHT: {
        popToManager = this.rightPop;
        targetPosition = { right: '0px', top: '35px' }; // 35px car le menu au dessus fait 34px au total
        break;
      }
    }

    if (PopUpManager.exist(popToManager)) {
      // suppression de l'instance existante
      popToManager.hide();
    }

    popToManager = new WizPopUp( // la taille sera donnée par le style (calc), 82px car le menu fait 34px et la barre d'info et le tab font 48px
      classFactory,
      false,
      true,
      null,
      '200px',
      'calc(100% - 92px)',
      null,
      null,
      targetPosition
    ).show();

    switch (
      panel // et on rend à césar (les instances)...
    ) {
      case SIDE_PANEL_NAME.LEFT: {
        this.leftPop = popToManager;
        break;
      }
      case SIDE_PANEL_NAME.RIGHT: {
        this.rightPop = popToManager;
        break;
      }
    }
  }

  /**
   * collecte des constructeurs (à venir) pour les panels gauche / droite
   * return un tableau à 2 entrées [ gauche | droite ]
   */
  private identifyFactory(): any[] {
    // on va construire l'instance depuis la factory
    // potentiellement plusieurs vues (navig niveau 2)
    // on prend la factory de plus haut niveau
    let leftFactory: any = null;
    let rightFactory: any = null;
    let currentDeep: number = 0;

    this.model.currentViews.forEach((value: BaseView, key: string) => {
      const viewDeep = WizNavigationManager.getNavigationLevel(value);
      if (viewDeep > currentDeep) {
        currentDeep = viewDeep;
        if (value.leftSideContent()) {
          leftFactory = value.leftSideContent();
        }
        if (value.rightSideContent()) {
          rightFactory = value.rightSideContent();
        }
      }
    });
    return [leftFactory, rightFactory];
  }

  /**
   * demande d'affichage/fermeture du panel de gauche
   */
  public showHideLeftSidePanel(): void {
    if (!PopUpManager.exist(this.leftPop)) {
      // show
      this.doShowLeftSidePanel();
    } else {
      // hide
      this.hideLeftSidePanel();
    }
  }

  /**
   * demande d'affichage/fermeture du panel de droite
   */
  public showHideRightSidePanel(): void {
    if (!PopUpManager.exist(this.rightPop)) {
      // show
      this.doShowRightSidePanel();
    } else {
      // hide
      this.hideRightSidePanel();
    }
  }

  /**
   * demande d'affichage du panneau de gauche
   */
  public showLeftSidePanel(): void {
    if (!PopUpManager.exist(this.leftPop)) {
      // show
      this.doShowLeftSidePanel();
    } else {
      // si existe, juste un refresh du show
      PopUpManager.refresh(this.leftPop);
    }
  }

  /**
   * demande d'affichage du panneau de droite
   */
  public showRightSidePanel(): void {
    if (!PopUpManager.exist(this.rightPop)) {
      // show
      this.doShowRightSidePanel();
    } else {
      // si existe, juste un refresh du show
      PopUpManager.refresh(this.rightPop);
    }
  }

  /**
   * demande de fermeture du panneau de gauche
   */
  public hideLeftSidePanel(): void {
    if (PopUpManager.exist(this.leftPop)) {
      this.leftPop.hide();
    }
  }

  /**
   * demande de fermeture du panneau de droite
   */
  public hideRightSidePanel(): void {
    if (PopUpManager.exist(this.rightPop)) {
      this.rightPop.hide();
    }
  }

  /**
   * affichage du panneau de gauche et mise à jour du contexte de la vue
   */
  private doShowLeftSidePanel(): void {
    const factory: any = this.identifyFactory()[0];
    if (factory) {
      this.buildAndShowSide(SIDE_PANEL_NAME.LEFT, factory);
    }
  }

  /**
   * affichage du panneau de droite et mise à jour du contexte de la vue
   */
  private doShowRightSidePanel(): void {
    const factory: any = this.identifyFactory()[1];
    if (factory) {
      this.buildAndShowSide(SIDE_PANEL_NAME.RIGHT, factory);
    }
  }

  /**
   * accesseur à la vue courante
   * le composant de plus bas niveau dans la map
   * @return BaseView, la vue ayant le niveau le plus bas qui correspond à la vue principale
   */
  public getCurrentView(): BaseView {
    // plus bas niveau dans la pile
    let currentDeep: number = 2;
    let currentView: BaseView;
    this.model.currentViews.forEach((value: BaseView, key: string) => {
      const viewDeep = WizNavigationManager.getNavigationLevel(value);
      if (currentDeep >= viewDeep) {
        currentDeep = viewDeep;
        currentView = value;
      }
    });
    return currentView;
  }

  /**
   * accesseur à la vue de BASE (niveau = 2). Elle porte les entrées de contexte
   * @return BaseView, la vue ayant le niveau 2
   */
  public getCurrentMainView(): BaseView {
    let currentView: BaseView;
    this.model.currentViews.forEach((value: BaseView, key: string) => {
      const viewDeep = WizNavigationManager.getNavigationLevel(value);
      // JRO : le niveau de profondeur d'une mainView passe à deux suite à l'ajout d'une route parent aux mains views pour la gestion du contexte
      if (viewDeep === 2) {
        currentView = value;
      }
    });
    return currentView;
  }

  /**
   * demande d'initialisation d'une création dans la vue
   */
  public doAskForAdd(): void {
    const view: BaseView = this.getCurrentMainView();
    if (view) {
      view.askForAddFromContext();
    }
  }

  /**
   * accesseur à la sous-vue courante
   * le composant de plus haut niveau dans la map
   * @return BaseView, la vue ayant le niveau le plus haut qui correspond à la sous-vue
   */
  public getCurrentSubView(): BaseView {
    // plus bas niveau dans la pile pour commencer, puis on remonte
    let currentDeep: number = NaN;
    let currentView: BaseView;
    this.model.currentViews.forEach((value: BaseView, key: string) => {
      const viewDeep = WizNavigationManager.getNavigationLevel(value);
      if (isNaN(currentDeep) || currentDeep <= viewDeep) {
        currentDeep = viewDeep;
        currentView = value;
      }
    });
    return currentView;
  }
}
